/**
 * アバターの見た目(docs/02_game_design.md §7.1)。
 *
 * ベースキャラの絵文字の上に、装備中パーツの絵文字をスロットごとの位置へ重ねる。
 * 画像素材ができたら `ItemIcon` が img に切り替わるだけで、この配置はそのまま使える。
 */

import { baseCharacterOf } from '../../logic/avatar';
import type { Catalog, ItemSlot } from '../../services/catalog';
import type { InventorySnapshot } from '../../services/inventory';

interface Props {
  readonly catalog: Catalog;
  readonly inventory: InventorySnapshot;
  readonly size?: 'sm' | 'md' | 'lg';
}

/** 重ね順(背面 → 前面)。 */
const LAYER_ORDER: readonly ItemSlot[] = ['background', 'effect', 'outfit', 'face', 'hat', 'hand'];

export function AvatarView({ catalog, inventory, size = 'md' }: Props) {
  const base = baseCharacterOf(inventory.base);
  const layers = LAYER_ORDER.map((slot) => {
    const itemId = inventory.equipped[slot];
    const item = itemId === undefined ? undefined : catalog.itemsById.get(itemId);
    return item ? { slot, item } : null;
  }).filter((layer): layer is { slot: ItemSlot; item: NonNullable<typeof layer>['item'] } =>
    Boolean(layer),
  );

  return (
    <div
      className={`avatar-view ${size}`}
      style={{ ['--avatar-color' as string]: base.color }}
      aria-label={`アバター ${base.name}`}
      data-base={base.id}
    >
      {layers.map(({ slot, item }) => (
        <span key={slot} className={`avatar-layer slot-${slot} rarity-${item.rarity}`}>
          {item.emoji}
        </span>
      ))}
      <span className="avatar-base">{base.emoji}</span>
    </div>
  );
}
